"use client"

import { useMemo, useState, useCallback, useEffect } from "react"
import { getParsedColors, onRawColorsChange, type ParsedColor } from "./colors"
import { parseColor, rgbToHsl } from "./colors"

const HUE_STEPS = 18

export function ChromaticCircle() {
    const [hoveredColor, setHoveredColor] = useState<ParsedColor | null>(null)
    const [selectedColor, setSelectedColor] = useState<ParsedColor | null>(null)
    const [version, setVersion] = useState(0)


    useEffect(() => {
        return onRawColorsChange(() => setVersion((v) => v + 1))
    }, [])

    const allColors = useMemo(() => getParsedColors(), [version])

    const sortedAll = useMemo(() => {
        return [...allColors].sort((a, b) => {
            const hueSlice = 360 / HUE_STEPS
            const aHue = Math.floor(a.h / hueSlice)
            const bHue = Math.floor(b.h / hueSlice)
            if (aHue !== bHue) return aHue - bHue
            return b.l - a.l
        })
    }, [allColors])

    // 👇 regra principal
    const displayedColor = selectedColor ?? hoveredColor

    const handleMouseEnter = useCallback((color: ParsedColor) => {
        if (!selectedColor) {
            setHoveredColor(color)
        }
    }, [selectedColor])

    const handleMouseLeave = useCallback(() => {
        if (!selectedColor) {
            setHoveredColor(null)
        }
    }, [selectedColor])

    const handleClick = useCallback((color: ParsedColor) => {
        setSelectedColor(color)
    }, []);

    const cmyk = useMemo(() => {
        if (!displayedColor) return null
        return rgbToCmyk(displayedColor.r, displayedColor.g, displayedColor.b)
    }, [displayedColor])

    function rgbToCmyk(r: number, g: number, b: number) {
        const rr = r / 255
        const gg = g / 255
        const bb = b / 255

        const k = 1 - Math.max(rr, gg, bb)
        if (k >= 1) return { c: 0, m: 0, y: 0, k: 1 } // preto puro

        const c = (1 - rr - k) / (1 - k)
        const m = (1 - gg - k) / (1 - k)
        const y = (1 - bb - k) / (1 - k)

        return { c, m, y, k }
    }

    useEffect(() => {
        if (typeof window === "undefined") return

        (window as any).setDisplayedColor = (colorString: string) => {
            const parsed = parseColor(colorString)
            if (!parsed) return

            const { r, g, b, a } = parsed
            const { h, s, l } = rgbToHsl(r, g, b)

            setSelectedColor({
                rgba: colorString,
                r,
                g,
                b,
                a,
                h,
                s,
                l,
            })
        }

        return () => {
            delete (window as any).setDisplayedColor
        }
    }, [])


    return (
        <div className="flex flex-col gap-6 w-full pl-3 pr-3">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-foreground">
                        
                    </h1>
                    <p className="mt-1 text-sm text-white">
                        {sortedAll.length} cores
                    </p>
                </div>

                {displayedColor && (
                    <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-white p-1 shadow-sm">
                        <div
                            className="h-10 w-10 shrink-0 rounded-md border border-neutral-200"
                            style={{ backgroundColor: displayedColor.rgba }}
                        />
                        <div className="flex flex-col text-xs font-mono leading-relaxed hidden md:flex">
                            <span className="text-neutral-800">{displayedColor.rgba}</span>

                            {/**<span className="text-neutral-500">
                                {"H:"}
                                {displayedColor.h.toFixed(0)}
                                {"  S:"}
                                {(displayedColor.s * 100).toFixed(0)}
                                {"%  L:"}
                                {(displayedColor.l * 100).toFixed(0)}
                                {"%"}
                            </span> */}

                            {cmyk && (
                                <span className="text-neutral-500">
                                    {"C:"}{Math.round(cmyk.c * 100)}{"%  "}
                                    {"M:"}{Math.round(cmyk.m * 100)}{"%  "}
                                    {"Y:"}{Math.round(cmyk.y * 100)}{"%  "}
                                    {"K:"}{Math.round(cmyk.k * 100)}{"%"}
                                </span>
                            )}
                        </div>

                    </div>

                )}
            </div>

            <div className="grid grid-cols-[repeat(auto-fill,minmax(28px,1fr))] gap-0.5 overflow-y-auto max-h-[200px] md:max-h-[70vh] overflow-x-hidden p-1">
                {sortedAll.map((color, i) => (
                    <div
                        key={i}
                        className="cor-tabela2 aspect-square rounded-sm cursor-pointer transition-transform hover:scale-150 hover:z-10 hover:shadow-md"
                        style={{
                            backgroundColor: color.rgba,
                            outline:
                                displayedColor?.rgba === color.rgba
                                    ? "2px solid #171717"
                                    : undefined,
                            outlineOffset:
                                displayedColor?.rgba === color.rgba ? 1 : undefined,
                        }}
                        onMouseEnter={() => handleMouseEnter(color)}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => handleClick(color)}
                    />
                ))}
            </div>
        </div>
    )
}
